import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { ChatHeader } from "@/components/chat/ChatHeader";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { ChatInput } from "@/components/chat/ChatInput";
import { TypingBubble } from "@/components/chat/TypingBubble";
import { useVoiceRecording } from "@/hooks/useVoiceRecording";

interface Message {
  id: string;
  content: string;
  is_from_user: boolean;
  created_at: string;
  type: string;
  image_url?: string;
}

const DAILY_MESSAGE_LIMIT = 50;
const DAILY_VOICE_LIMIT = 10;

const formatConversationHistory = (messages: Message[]): string => {
  return messages
    .map(msg => {
      let content = msg.content;
      if (msg.image_url) {
        content = `[Image: ${msg.image_url}]\n${content}`;
      }
      return `${msg.is_from_user ? "user" : "bot"}: ${content}`;
    })
    .join("\n\n");
};

const Chat = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [dailyMessageCount, setDailyMessageCount] = useState(0);
  const [dailyVoiceCount, setDailyVoiceCount] = useState(0);
  const [dailyImageCount, setDailyImageCount] = useState(0);
  const [dailyImageLimit, setDailyImageLimit] = useState(15);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [selectedBot, setSelectedBot] = useState("01394a4e-bac9-4d73-a907-ffb6d7891f37");
  const [dailyMessageLimit, setDailyMessageLimit] = useState(30);
  const [userPlan, setUserPlan] = useState<string>("free");
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  
  const {
    isRecording,
    recordingTime,
    isConverting,
    startRecording,
    stopRecording,
    getRecordingData
  } = useVoiceRecording();

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/");
        return;
      }
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('plan')
        .eq('id', session.user.id)
        .single();
      
      setUserPlan(profile?.plan || 'free');
      
      const { data: imageCount } = await supabase.rpc('get_user_daily_image_message_count', {
        user_id_param: session.user.id
      });
      
      setDailyImageCount(imageCount || 0);
      setDailyImageLimit(profile?.plan === 'basic' ? 15 : 0);
      
      const { data: limit } = await supabase.rpc('get_plan_message_limit', {
        user_id_param: session.user.id
      });
      setDailyMessageLimit(limit || 30);
      
      loadMessages(session.user.id);
      updateDailyMessageCount(session.user.id);
      updateDailyVoiceCount(session.user.id);
    };
    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        navigate("/");
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  const updateDailyVoiceCount = async (userId: string) => {
    const { data, error } = await supabase.rpc('get_user_daily_voice_message_count', {
      user_id_param: userId
    });

    if (error) {
      console.error("Error getting voice message count:", error);
      return;
    }

    setDailyVoiceCount(data || 0);
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const updateDailyMessageCount = async (userId: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const { count, error } = await supabase
      .from("maibot_messages")
      .select("*", { count: 'exact', head: true })
      .eq("user_id", userId)
      .gte("created_at", today.toISOString());

    if (error) {
      console.error("Error getting message count:", error);
      return;
    }

    setDailyMessageCount(count || 0);
  };

  const handleVoiceTranscriptionError = (error: any) => {
    let errorMessage = "Failed to convert speech to text. Please try again.";
    
    if (error.message?.includes('quota') || error.status === 429) {
      errorMessage = "Voice recognition is temporarily unavailable. Please try again later or use text input.";
      setIsVoiceMode(false);
    }
    
    toast({
      title: "Error",
      description: errorMessage,
      variant: "destructive",
    });
  };

  const loadMessages = async (botId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase
      .from("maibot_messages")
      .select("*")
      .order("created_at", { ascending: true });

    if (error) {
      toast({
        title: "Error loading messages",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setMessages(data as Message[]);
  };

  const playAudioResponse = async (text: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('text_to_speech', {
        body: { text, voice: 'alloy' }
      });

      if (error) {
        console.error('Error generating speech:', error);
        return;
      }

      const audio = new Audio(`data:audio/mp3;base64,${data.audioContent}`);
      await audio.play();
    } catch (error) {
      console.error('Error playing audio:', error);
    }
  };

  const generateBotResponse = async (userMessage: string, imageFile: File | null = null) => {
    try {

      // Get conversation history based on user plan
      const historyLimit = userPlan === 'basic' ? 6 : 2;
      const conversationHistory = messages.slice(-historyLimit);

      const fullHistory = [
        ...conversationHistory,
        { 
          id: 'temp',
          content: userMessage, 
          is_from_user: true,
          created_at: new Date().toISOString(),
          type: "text"
        }
      ];
      
      const formattedHistory = formatConversationHistory(fullHistory);
      console.log('Sending conversation history:', formattedHistory);

      let response;
      
      if (imageFile) {
        const formData = new FormData();
        formData.append('bot_id', selectedBot);
        formData.append('message', formattedHistory);
        formData.append('image', imageFile);
        
        response = await fetch(
          'https://maibot-backend.onrender.com/generate_text_v2',
          {
            method: 'POST',
            body: formData,
            credentials: 'include',
          }
        );
      } else {
        const payload = {
          message: formattedHistory,
          bot_id: selectedBot,
          chat_history: ""
        };

        response = await fetch(
          'https://maibot-backend.onrender.com/generate_text',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
            credentials: 'include',
          }
        );
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(`Failed to generate response: ${errorText}`);
      }

      const data = await response.json();
      console.log('API Response:', data);
      
      if (!data || !data.output) {
        throw new Error('Invalid response format from API');
      }
      
      let cleanedOutput = data.output;
      if (cleanedOutput.toLowerCase().startsWith('bot:')) {
        cleanedOutput = cleanedOutput.substring(4).trim();
      }

      const messageType = isVoiceMode ? "voice" : "text";
      
      if (isVoiceMode) {
        await playAudioResponse(cleanedOutput);
      }

      return cleanedOutput;
    } catch (error) {
      console.error('Error generating response:', error);
      toast({
        title: "Error generating response",
        description: error instanceof Error ? error.message : "Failed to generate response",
        variant: "destructive"
      });
      return "I apologize, but I'm having trouble connecting to my brain at the moment. Please try again later.";
    }
  };

  const saveMessage = async (content: string, isFromUser: boolean, messageType: string = "text", imageUrl?: string | null) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;

    if (isFromUser) {
      if (dailyMessageCount >= dailyMessageLimit) {
        toast({
          title: "Daily limit reached",
          description: "You've reached your daily message limit. Please try again tomorrow.",
          variant: "destructive",
        });
        return null;
      }

      if (messageType === "voice" && dailyVoiceCount >= DAILY_VOICE_LIMIT) {
        toast({
          title: "Voice message limit reached",
          description: "You've reached your daily voice message limit. Please try again tomorrow or use text input.",
          variant: "destructive",
        });
        setIsVoiceMode(false);
        return null;
      }
    }

    const { data, error } = await supabase
      .from("maibot_messages")
      .insert([
        {
          content,
          is_from_user: isFromUser,
          user_id: session.user.id,
          user_email: session.user.email,
          type: messageType,
          image_url: imageUrl
        },
      ])
      .select()
      .single();

    if (error) {
      toast({
        title: "Error saving message",
        description: error.message,
        variant: "destructive",
      });
      return null;
    }

    if (isFromUser) {
      setDailyMessageCount(prev => prev + 1);
      if (messageType === "voice") {
        setDailyVoiceCount(prev => prev + 1);
      }
    }

    return data as Message;
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('chat-images')
        .upload(fileName, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data } = supabase.storage
        .from('chat-images')
        .getPublicUrl(fileName);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image. Please try again.",
        variant: "destructive",
      });
      return null;
    }
  };

  const handleImageSelect = (file: File) => {
    if (userPlan === 'free') {
      toast({
        title: "Tính năng không khả dụng",
        description: "Bạn phải nâng cấp để gửi ảnh",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "Image size should be less than 5MB",
        variant: "destructive",
      });
      return;
    }
    setSelectedImage(file);
    const url = URL.createObjectURL(file);
    setImagePreviewUrl(url);
  };

  const handleVoiceModeToggle = (enabled: boolean) => {
    if (enabled && userPlan === 'free') {
      toast({
        title: "Tính năng không khả dụng",
        description: "Bạn phải nâng cấp để thu âm",
        variant: "destructive",
      });
      return;
    }
    setIsVoiceMode(enabled);
  };

  const handleClearImage = () => {
    setSelectedImage(null);
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedImage) || isLoading) return;

    if (dailyMessageCount >= dailyMessageLimit) {
      toast({
        title: "Daily limit reached",
        description: `You've reached your daily message limit of ${dailyMessageLimit} messages. Please try again tomorrow.`,
        variant: "destructive",
      });
      return;
    }

    if (selectedImage && dailyImageCount >= dailyImageLimit) {
      toast({
        title: "Image limit reached",
        description: "You've reached your daily image message limit. Please try again tomorrow.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      let imageUrl = null;
      if (selectedImage) {
        imageUrl = await uploadImage(selectedImage);
        if (imageUrl) {
          setDailyImageCount(prev => prev + 1);
        }
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/");
        return;
      }

      const messageContent = selectedImage ? newMessage || "Image message" : newMessage.trim();
      console.log('Message content:', messageContent);
      
      const savedUserMessage = await saveMessage(messageContent, true, "text", imageUrl);
      if (savedUserMessage) {
        setMessages((prev) => [...prev, savedUserMessage]);
        setNewMessage("");
        handleClearImage();
      }

      const botResponse = await generateBotResponse(messageContent, selectedImage);
      const savedBotMessage = await saveMessage(botResponse, false, "text");
      if (savedBotMessage) {
        setMessages((prev) => [...prev, savedBotMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send message",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVoiceMessage = async () => {
    try {
      const recordingData = await getRecordingData();
      if (!recordingData) return;

      if (dailyVoiceCount >= DAILY_VOICE_LIMIT) {
        toast({
          title: "Voice message limit reached",
          description: "You've reached your daily voice message limit. Please try again tomorrow or use text input.",
          variant: "destructive",
        });
        setIsVoiceMode(false);
        return;
      }

      const { chunks, mimeType } = recordingData;
      const audioBlob = new Blob(chunks, { type: mimeType });
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result?.toString().split(',')[1];
        if (base64Audio) {
          try {
            const { data, error } = await supabase.functions.invoke('speech_to_text', {
              body: { 
                audio: base64Audio,
                mimeType
              }
            });

            if (error) {
              handleVoiceTranscriptionError(error);
              return;
            }

            if (data?.text) {
              setIsLoading(true);
              try {
                const savedUserMessage = await saveMessage(data.text, true, "voice");
                if (savedUserMessage) {
                  setMessages((prev) => [...prev, savedUserMessage]);
                }

                const botResponse = await generateBotResponse(data.text);
                const savedBotMessage = await saveMessage(botResponse, false, "text");
                if (savedBotMessage) {
                  setMessages((prev) => [...prev, savedBotMessage]);
                }
              } catch (error) {
                toast({
                  title: "Error",
                  description: "Failed to send message",
                  variant: "destructive",
                });
              } finally {
                setIsLoading(false);
              }
            }
          } catch (error) {
            handleVoiceTranscriptionError(error);
          }
        }
      };
    } catch (error) {
      console.error('Error processing voice message:', error);
      handleVoiceTranscriptionError(error);
    }
  };

  useEffect(() => {
    if (!isRecording && getRecordingData()) {
      handleVoiceMessage();
    }
  }, [isRecording]);

  const handleBotChange = async (botId: string) => {
    setSelectedBot(botId);
  };

  return (
    <div className="fixed inset-0 bg-background">
      <div className="h-full flex flex-col max-w-4xl mx-auto">
        <div className="sticky top-0 z-50 flex-shrink-0 px-4 py-2 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <ChatHeader
            remainingMessages={dailyMessageLimit - dailyMessageCount}
            remainingVoiceMessages={DAILY_VOICE_LIMIT - dailyVoiceCount}
            isVoiceMode={isVoiceMode}
            setIsVoiceMode={handleVoiceModeToggle}
            dailyMessageLimit={dailyMessageLimit}
            selectedBot={selectedBot}
            onBotChange={handleBotChange}
          />
        </div>

        <div className="flex-1 overflow-hidden">
          <ScrollArea 
            ref={scrollAreaRef} 
            className="h-full px-4"
            style={{ touchAction: 'pan-y' }}
          >
            <div className="space-y-4 py-4">
              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))}
              {isLoading && <TypingBubble />}
            </div>
          </ScrollArea>
        </div>

        <div className="sticky bottom-0 left-0 right-0 z-50 border-t border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="max-w-4xl mx-auto">
            <ChatInput
              isVoiceMode={isVoiceMode}
              isRecording={isRecording}
              isConverting={isConverting}
              recordingTime={recordingTime}
              newMessage={newMessage}
              isLoading={isLoading}
              dailyMessageCount={dailyMessageCount}
              dailyVoiceCount={dailyVoiceCount}
              dailyMessageLimit={dailyMessageLimit}
              dailyVoiceLimit={DAILY_VOICE_LIMIT}
              dailyImageCount={dailyImageCount}
              dailyImageLimit={dailyImageLimit}
              setNewMessage={setNewMessage}
              handleSendMessage={handleSendMessage}
              startRecording={startRecording}
              stopRecording={stopRecording}
              formatTime={formatTime}
              onImageSelect={handleImageSelect}
              selectedImage={selectedImage}
              clearSelectedImage={handleClearImage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
